import {
	AppBar,
	Box,
	CircularProgress,
	Collapse,
	Drawer,
	IconButton,
	Paper,
	Stack,
	Toolbar,
	Typography,
	useMediaQuery,
	useTheme
} from '@mui/material';
import { Menu as MenuIcon } from '@mui/icons-material';
import React, {
	ReactNode,
	useCallback,
	useEffect,
	useMemo,
	useState
} from 'react';
import { Helmet } from 'react-helmet-async';
import { useAdminPortalConfig } from 'api/adminPortalConfig';
import { StorageKey, QueryParam } from 'consts/keys';
import useQueryParams from 'hooks/useQueryParams';
import { useAppInitError, useAppInitialized } from 'state/appInit';
import { useProjectCtx } from 'state/project/ProjectCtx';
import ThemeModeButton from 'state/theme/ThemeSwitch';
import UserMenu from 'app/Layout/UserMenu';
import { layoutContainerSx } from 'app/Layout/containerStyles';
import NoAuthView from 'app/Views/NoAuthView';
import { NoAccessReason } from 'types/access';
import ErrorView from 'app/Views/ErrorView';
import { useErrorCtx } from 'state/error/ErrorCtx';
import { ErrorBoundary } from 'react-error-boundary';

interface IAppLayoutProps {
	children?: ReactNode;
	sideBar?: ReactNode;
}

const AppLayout: React.FC<IAppLayoutProps> = ({
	children = null,
	sideBar = null
}) => {
	const appInitialized = useAppInitialized();
	const appInitError = useAppInitError();
	const { projectId } = useProjectCtx();
	const { data: adminPortalConfig } = useAdminPortalConfig();
	const { error } = useErrorCtx();
	const muiTheme = useTheme();

	const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
	const [mobileOpen, setMobileOpen] = useState(false);
	const closeSidebar = useCallback(() => setMobileOpen(false), []);

	// Desktop sidebar initial state: `sidebarOpen` query param (startup only,
	// then dropped) -> localStorage -> open. Toggle persists to localStorage.
	const [sidebarOpenParam, setSidebarOpenParam] = useQueryParams<
		'true' | 'false'
	>(QueryParam.SidebarOpen);
	const [desktopOpen, setDesktopOpen] = useState(() => {
		if (sidebarOpenParam != null) {
			return sidebarOpenParam === 'true';
		}
		const stored = localStorage.getItem(StorageKey.SidebarOpen);
		return stored != null ? stored === 'true' : true;
	});
	useEffect(() => {
		// consume the startup param, then drop it from the URL
		if (sidebarOpenParam != null) {
			setSidebarOpenParam(null);
		}
	}, [sidebarOpenParam, setSidebarOpenParam]);
	const toggleDesktopSidebar = useCallback(
		() =>
			setDesktopOpen((open) => {
				const next = !open;
				localStorage.setItem(StorageKey.SidebarOpen, String(next));
				return next;
			}),
		[]
	);

	const logo = muiTheme.assets?.logo;
	const favicon = muiTheme.assets?.favicon;
	const loader = muiTheme.portalStyles?.loader;
	const appTitle =
		adminPortalConfig?.appTitle || intl.tr('general.adminPortal');

	const faviconUrl = useMemo(
		() =>
			(favicon?.startsWith('url(')
				? favicon?.replace(/^url\((['"]?)(.*?)\1\)$/, '$2')
				: favicon) || null,
		[favicon]
	);

	const sidebarPaper = (
		<Paper
			elevation={0}
			data-testid="app-layout-sidebar"
			sx={{
				...layoutContainerSx.adminPortalSidebarPanel(muiTheme),
				minWidth: 320,
				maxWidth: 320,
				height: '100%',
				flexShrink: 0,
				p: 2,
				overflow: 'hidden'
			}}
		>
			{sideBar}
		</Paper>
	);
	const logoUrl = useMemo(
		() =>
			(logo?.startsWith('url(')
				? logo?.replace(/^url\((['"]?)(.*?)\1\)$/, '$2')
				: logo) || null,
		[logo]
	);

	return (
		<Paper
			elevation={0}
			data-testid="app-layout"
			sx={{
				...layoutContainerSx.adminPortalAppBackground(muiTheme),
				height: '100%',
				width: '100%',
				borderRadius: 0
			}}
		>
			{faviconUrl && (
				<Helmet>
					<link rel="icon" type="image/png" href={faviconUrl} sizes="16x16" />
					<title>{appTitle}</title>
				</Helmet>
			)}
			<Stack
				direction="column"
				alignItems="center"
				justifyContent="center"
				data-testid="app-init-loader"
				sx={{
					position: 'absolute',
					top: 0,
					left: 0,
					width: '100%',
					height: '100%',
					backgroundColor:
						muiTheme.palette.mode === 'dark' ? '#121212' : '#fdfdfd',
					zIndex: muiTheme.zIndex.drawer + 1,
					color: muiTheme.palette.mode === 'dark' ? 'white' : 'black',
					transition: 'opacity 200ms 100ms ease-in-out, visibility 300ms',
					opacity: appInitialized ? 0 : 1,
					visibility: appInitialized ? 'hidden' : 'visible',
					pointerEvents: 'none'
				}}
			>
				{loader?.url ? (
					<Box
						component="img"
						src={loader.url}
						alt=""
						data-testid="app-init-loader-image"
						sx={{ maxWidth: 120, maxHeight: 120, objectFit: 'contain' }}
					/>
				) : (
					<CircularProgress size={50} thickness={5} sx={{ color: 'inherit' }} />
				)}
			</Stack>
			<Stack direction="column" height="100%" alignItems="center">
				<AppBar
					position="static"
					color="default"
					elevation={0}
					sx={{
						...layoutContainerSx.adminPortalHeader(muiTheme),
						boxShadow: 'none'
					}}
				>
					<Toolbar
						sx={{
							px: 4,
							justifyContent: 'space-between',
							minHeight: {
								xs: 54
							}
						}}
					>
						<Stack direction="row" alignItems="center" gap={2}>
							{sideBar && (
								<IconButton
									edge="start"
									size="small"
									aria-label={intl.tr('general.openMenu')}
									data-testid="sidebar-toggle-button"
									onClick={() =>
										isMobile ? setMobileOpen(true) : toggleDesktopSidebar()
									}
								>
									<MenuIcon />
								</IconButton>
							)}
							{logoUrl && (
								<Box
									component="img"
									src={logoUrl}
									alt="Logo"
									sx={{
										height: 32,
										maxWidth: 200,
										objectFit: 'contain'
									}}
								/>
							)}
							<Typography variant="subtitle2">{appTitle}</Typography>
						</Stack>
						{projectId ? <UserMenu /> : <ThemeModeButton />}
					</Toolbar>
				</AppBar>
				<Stack
					direction="row"
					gap={{ xs: 3, md: 0 }}
					p={{ xs: 0, sm: 2, md: 3 }}
					width="100%"
					sx={{ flex: 1, minHeight: 0 }}
				>
					<ErrorBoundary fallbackRender={ErrorView}>
						{sideBar &&
							(isMobile ? (
								<Drawer
									anchor="left"
									open={mobileOpen}
									onClose={closeSidebar}
									data-testid="app-layout-sidebar-drawer"
									slotProps={{
										paper: {
											sx: {
												borderTopLeftRadius: 0,
												borderBottomLeftRadius: 0
											}
										}
									}}
								>
									<Box
										role="presentation"
										onClick={closeSidebar}
										data-testid="app-layout-sidebar"
										sx={{
											...layoutContainerSx.adminPortalSidebarPanel(muiTheme),
											width: 320,
											p: 2
										}}
									>
										{sideBar}
									</Box>
								</Drawer>
							) : (
								// Desktop: horizontal Collapse slides the panel in. Force full
								// height through Collapse's wrapper divs (they don't inherit the
								// row's flex-stretch); transparent wrapperInner `pr` puts the
								// column gap inside the collapse so it animates with the width.
								<Collapse
									orientation="horizontal"
									in={desktopOpen}
									unmountOnExit
									data-testid="app-layout-sidebar-collapse"
									sx={{
										flexShrink: 0,
										height: '100%',
										'& .MuiCollapse-wrapper, & .MuiCollapse-wrapperInner': {
											height: '100%'
										},
										'& .MuiCollapse-wrapperInner': { pr: 3 }
									}}
								>
									{sidebarPaper}
								</Collapse>
							))}
						<Paper
							elevation={0}
							sx={{
								...layoutContainerSx.adminPortalContentPanel(muiTheme),
								flexGrow: 1,
								p: 2,
								height: '100%',
								overflow: 'auto'
							}}
						>
							{error && <ErrorView error={error} />}
							{appInitError && (
								<NoAuthView noAccessReason={NoAccessReason.APP_LOAD_ERROR} />
							)}
							{!error && !appInitError && children}
						</Paper>
					</ErrorBoundary>
				</Stack>
			</Stack>
		</Paper>
	);
};

export default AppLayout;
